'use client';

import { useEffect, useMemo, useState } from 'react';
import Header from '@/components/Header';
import Navigation from '@/components/Navigation';
import JourneyCTA from '@/components/JourneyCTA';
import { useAuth } from '@/contexts/AuthContext';
import { purchaseIntentService } from '@/services/purchaseIntentService';
import { paymentService } from '@/services/paymentService';
import type { ActivePackage } from '@/types/package';
import { buildMembershipFaqAnswer, getOneYearPackage } from '@/utils/membershipUtils';
import { trackSelectItem } from '@/utils/analytics';
import { membershipFaqs } from './membershipFaqs';
import { buildWhatsAppSupportUrl } from '@/utils/supportContactUtils';
import MembershipPricingSection from './MembershipPricingSection';
import { MembershipFaqSkeleton } from './MembershipSkeleton';
import { Phone, MessageCircle, Crown, Zap, Heart, Shield, Users, Clock } from 'lucide-react';

export default function MembershipClient() {
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);
  const [packages, setPackages] = useState<ActivePackage[]>([]);
  const [isLoadingPackages, setIsLoadingPackages] = useState(true);
  const [packagesError, setPackagesError] = useState<string | null>(null);
  const { isAuthenticated, isLoading: authLoading, user } = useAuth();

  useEffect(() => {
    let isMounted = true;

    const loadPackages = async () => {
      setIsLoadingPackages(true);
      setPackagesError(null);

      try {
        const activePackages = await paymentService.getActivePackages(1, 10);
        if (isMounted) {
          setPackages(activePackages);
        }
      } catch {
        if (isMounted) {
          setPackages([]);
          setPackagesError('Unable to load the latest membership plans right now.');
        }
      } finally {
        if (isMounted) {
          setIsLoadingPackages(false);
        }
      }
    };

    void loadPackages();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (!isAuthenticated || !user?.id) return;
    if (!purchaseIntentService.canTrackNow(user.id, 'package_page')) return;

    void purchaseIntentService.trackPurchaseIntent(
      {
        stage: 'package_page',
        platform: 'web',
        appVersion: process.env.NEXT_PUBLIC_APP_VERSION || 'web',
        metadata: {
          screen: 'membership',
        },
      },
      user.id
    );
  }, [isAuthenticated, user?.id]);

  const oneYearPackage = useMemo(() => getOneYearPackage(packages), [packages]);

  const faqItems = useMemo(
    () =>
      membershipFaqs.map((faq, index) =>
        index === 0
          ? {
              ...faq,
              answer: buildMembershipFaqAnswer(faq.answer, oneYearPackage),
            }
          : faq
      ),
    [oneYearPackage]
  );

  const handleSelectPackage = (packageId: string) => {
    const selectedPackage = packages.find((pkg) => pkg.id === packageId);
    if (selectedPackage) {
      trackSelectItem({
        itemId: selectedPackage.id,
        itemName: selectedPackage.name,
        price: selectedPackage.offerPrice,
      });
    }

    window.location.href = `/order-summary?package=${packageId}`;
  };

  const showPricingSkeleton = authLoading || isLoadingPackages;

  return (
    <div className="bg-gradient-to-br from-blue-50 via-white to-purple-50 min-h-screen">
      {/* Header */}
      <Header variant="fixed" />
      
      {/* Navigation Menu for Logged-in Users */}
      {isAuthenticated && <Navigation />}

      {/* Membership Plans Comparison */}
      <section className={`${isAuthenticated ? 'pt-20 md:pt-44' : 'pt-20 md:pt-32'} pb-20`}>
        <div className="container mx-auto px-4">
          <div className="mb-16 mt-[52px] text-center">
            <h1 className="hero-heading mb-4 text-[1.5rem] font-bold text-gray-900 md:text-[1.8rem]">
              Choose Your Perfect Membership
            </h1>
            <p className="text-lg max-[599px]:text-[1.1rem] text-gray-600 max-w-3xl mx-auto">
              Unlock premium features to find your soulmate faster. Get access to advanced matching, 
              direct communication, and personalized support.
            </p>
          </div>

          <MembershipPricingSection
            packages={packages}
            isLoading={showPricingSkeleton}
            loadError={packagesError}
            isAuthenticated={isAuthenticated}
            onSelectPackage={handleSelectPackage}
          />
        </div>
      </section>

      {/* Features Comparison */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-16">
            What You Get with Premium Membership
          </h2>
          
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 max-[599px]:grid-cols-1 gap-8">
                             {/* Feature 1 */}
               <div className="text-center p-6">
                 <div className="w-20 h-20 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                   <Users className="w-10 h-10 text-primary-600" />
                 </div>
                 <h3 className="text-xl font-semibold text-gray-900 mb-3">View Contacts</h3>
                 <p className="text-gray-600">
                   Access contact information and phone numbers of potential matches to connect directly.
                 </p>
               </div>

              {/* Feature 2 */}
              <div className="text-center p-6">
                <div className="w-20 h-20 bg-secondary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <MessageCircle className="w-10 h-10 text-secondary-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Direct Communication</h3>
                <p className="text-gray-600">
                  Send messages and chat directly with potential matches without any restrictions.
                </p>
              </div>

              {/* Feature 3 */}
              <div className="text-center p-6">
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Heart className="w-10 h-10 text-green-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Kundali Matching</h3>
                <p className="text-gray-600">
                  Advanced horoscope compatibility checking for better marital harmony and understanding.
                </p>
              </div>

              {/* Feature 4 */}
              <div className="text-center p-6">
                <div className="w-20 h-20 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Zap className="w-10 h-10 text-yellow-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Quick Support</h3>
                <p className="text-gray-600">
                  Priority customer support and quick response times.
                </p>
              </div>

              {/* Feature 5 */}
              <div className="text-center p-6">
                <div className="w-20 h-20 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Crown className="w-10 h-10 text-purple-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Premium Features</h3>
                <p className="text-gray-600">
                  Access to exclusive broadcasts, profile highlighting, and advanced matching algorithms.
                </p>
              </div>

              {/* Feature 6 */}
              <div className="text-center p-6">
                <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Shield className="w-10 h-10 text-red-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Privacy & Security</h3>
                <p className="text-gray-600">
                  Enhanced privacy controls and verified profiles for safe and secure matchmaking.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Support Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-16">
            Get Help When You Need It
          </h2>
          
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 max-[599px]:grid-cols-1 gap-8">
              {/* Quick Call Support */}
              <div className="bg-white p-8 max-[599px]:p-6 rounded-2xl shadow-lg text-center">
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Phone className="w-10 h-10 text-green-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Quick Call Support</h3>
                <p className="text-gray-600 mb-6">
                  Speak directly with our relationship experts for personalized guidance and support.
                </p>
                <div className="space-y-3 text-left">
                  <div className="flex items-center gap-3">
                    <Clock className="w-5 h-5 text-green-600" />
                    <span className="text-gray-700">10.30 AM to 6 PM on Monday To Saturday</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Users className="w-5 h-5 text-green-600" />
                    <span className="text-gray-700">Expert counselors</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <MessageCircle className="w-5 h-5 text-green-600" />
                    <span className="text-gray-700">Free consultation</span>
                  </div>
                </div>
                <a 
                  href={`tel:${process.env.NEXT_PUBLIC_SUPPORT_PHONE_NUMBER || '919326013123'}`} 
                  className="w-full mt-6 bg-green-600 hover:bg-green-700 text-white py-3 px-6 rounded-xl font-semibold transition-colors inline-block text-center"
                >
                  Call Now: {process.env.NEXT_PUBLIC_SUPPORT_PHONE_NUMBER || '+91-9326013123'}
                </a>
              </div>

              {/* Quick Chat Support */}
              <div className="bg-white p-8 max-[599px]:p-6 rounded-2xl shadow-lg text-center">
                <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <MessageCircle className="w-10 h-10 text-blue-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Quick Chat Support</h3>
                <p className="text-gray-600 mb-6">
                  Get instant help through our live chat system with real-time responses.
                </p>
                <div className="space-y-3 text-left">
                  <div className="flex items-center gap-3">
                    <Zap className="w-5 h-5 text-blue-600" />
                    <span className="text-gray-700">Instant responses</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Shield className="w-5 h-5 text-blue-600" />
                    <span className="text-gray-700">Secure chat</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Users className="w-5 h-5 text-blue-600" />
                    <span className="text-gray-700">Trained agents</span>
                  </div>
                </div>
                <a 
                  href={buildWhatsAppSupportUrl('Hi')}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full mt-6 bg-blue-600 hover:bg-blue-700 text-white py-3 px-6 rounded-xl font-semibold transition-colors inline-block text-center"
                >
                  Start Chat Now
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-16">
            Frequently Asked Questions
          </h2>
          
          {authLoading || showPricingSkeleton ? (
            <MembershipFaqSkeleton />
          ) : (
          <div className="max-w-4xl mx-auto">
            <div className="space-y-4">
              {faqItems.map((faq, index) => (
                <div key={index} className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
                  <button
                    onClick={() => setOpenFAQ(openFAQ === index ? null : index)}
                    className="w-full px-6 max-[599px]:px-4 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
                  >
                    <h3 className="text-lg font-semibold text-gray-900">
                      {faq.question}
                    </h3>
                    <svg
                      className={`w-5 h-5 text-gray-500 transition-transform duration-300 ${
                        openFAQ === index ? 'rotate-180' : ''
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  <div
                    className={`px-6 max-[599px]:px-4 transition-all duration-300 ease-in-out ${
                      openFAQ === index ? 'pb-4 max-h-96 opacity-100' : 'max-h-0 opacity-0 overflow-hidden'
                    }`}
                  >
                    <p className="text-gray-600 leading-relaxed py-3">
                      {faq.answer}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          )}
        </div>
      </section>

      {/* Contact CTA */}
      <JourneyCTA 
        title="Ready to Start Your Journey?"
        description="Discover meaningful connections through our trusted Marathi matrimony platform built with the Marathi community's values at heart"
        primaryButtonText="Register Now in 2 minutes"
        primaryButtonLink="/register"
      />

    </div>
  );
}
